// src/components/App/GoogleAuthSuccessHandler.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import useAuth from '../../hooks/useAuth';
import SpinningCustomLogo from '../Common/SpinningCustomLogo';
import { confirmGoogleLink } from '../../ctapi/calendarService';

/**
 * This component is a visible confirmation page for when a user
 * successfully links their Google Calendar. It checks for an access token
 * or code in the URL, finalizes the link with the backend, and then redirects.
 */
export default function GoogleAuthSuccessHandler() {
    const { refreshUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        // This effect runs once on component mount
        const finalizeConnection = async () => {
            // DEBUG: Log all parameters received from Google
            console.log("GoogleAuthSuccessHandler params:", Object.fromEntries([...searchParams]));

            // Check for various token names that might be returned
            const token = searchParams.get('token') || searchParams.get('access_token') || searchParams.get('code');
            const isLinked = searchParams.get('linked'); // Flag from ConfirmHandler

            // DEBUG: Log the specific token extracted
            console.log("Extracted token/code:", token);

            // If we have a token and haven't already processed a confirmation
            if (token && !isLinked) {
                try {
                    setIsProcessing(true);
                    // Pass the token to the backend to finalize/verify the connection
                    await confirmGoogleLink(token);
                } catch (err) {
                    console.error("Error confirming Google link:", err);
                    // We continue to refreshUser to see if the session updated anyway
                }
            }

            // Refresh user data to get the new google_id / connected status
            await refreshUser();

            navigate('/settings', {
                replace: true,
                state: {
                    defaultSection: 'integrations',
                    googleAuthSuccess: true
                }
            });
        };

        // Add a small delay for UX so the success message is readable
        const timer = setTimeout(() => {
            finalizeConnection();
        }, 1500); 

        return () => clearTimeout(timer); 
    }, [navigate, refreshUser, searchParams]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: 'background.default',
                p: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    maxWidth: '450px'
                }}
            >
                <SpinningCustomLogo size={56} />
                <Typography variant="h5" sx={{ mt: 3, fontWeight: 500 }}>
                    {isProcessing ? 'Finalizing...' : 'Success!'}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {isProcessing 
                        ? 'Verifying your account details...' 
                        : 'Your Google Calendar has been linked. Redirecting you back to your settings...'}
                </Typography>
            </Paper>
        </Box>
    );
}